import { useCallback, useState } from 'react';
import { createDottedKeyObject, createObjectFromDottedKeys } from '@helpers';

import { Handlers, UseFormResult } from './types';
import { useUpdateEffect } from '../useUpdateEffect/useUpdateEffect';

export const useForm = (initialState: Record<string, unknown>): UseFormResult => {
    const [values, setValues] = useState(createDottedKeyObject(initialState));
    const [errorMessages, setErrorMessages] = useState({});
    const [formKey, setFormKey] = useState(0);

    useUpdateEffect(() => {
        setValues(createDottedKeyObject(initialState));
    }, [initialState]);

    const addErrorMessage = useCallback((key: string, message: string) => {
        setErrorMessages((error) => ({ ...error, [key]: message }));
    }, []);

    const getPeriodFromDates: Handlers['getPeriodFromDates'] = useCallback(
        (startDate, endDate) => ({
            startDate,
            endDate,
        }),
        [],
    );

    const getBlobValues: Handlers['getBlobValues'] = useCallback(
        (filename, id) => ({
            filename,
            id,
        }),
        [],
    );

    const handleFocus: Handlers['handleFocus'] = useCallback(
        (name, onFocus) => (event) => {
            addErrorMessage(name, '');
            onFocus && onFocus(event);
        },
        [addErrorMessage],
    );

    const handleFormSubmit: Handlers['handleFormSubmit'] = useCallback(
        (onSubmit) => (event) => {
            event.preventDefault();
            onSubmit(createObjectFromDottedKeys(values), event);
        },
        [values],
    );

    const handleFormReset: Handlers['handleFormReset'] = useCallback(
        (onReset) => (event) => {
            event.preventDefault();
            setValues({});
            setErrorMessages({});
            setFormKey((key) => key + 1);
            onReset && onReset(event);
        },
        [],
    );

    const handleChange: Handlers['handleChange'] = useCallback((name, value) => {
        setValues((val) => ({ ...val, [name]: value }));
    }, []);

    const handleTextChange: Handlers['handleTextChange'] = useCallback(
        (name, onChange) => (event) => {
            const { value } = event.target as HTMLInputElement;
            setValues((val) => ({ ...val, [name]: value }));
            onChange && onChange(event);
        },
        [],
    );

    const handleTagChange: Handlers['handleTagChange'] = useCallback(
        (name, onChange) => (event) => {
            const { value } = event;
            setValues((val) => ({ ...val, [name]: value }));
            onChange && onChange(event);
        },
        [],
    );

    const handleNumberChange: Handlers['handleNumberChange'] = useCallback(
        (name, onChange) => (event) => {
            const { value } = event.target as HTMLInputElement;
            setValues((val) => ({ ...val, [name]: value !== '' ? Number(value) : undefined }));
            onChange && onChange(event);
        },
        [],
    );

    // const handleValueChange: Handlers['handleSingleSelectChange'] = useCallback(
    //     name => value => setValues(val => ({ ...val, [name]: value })),
    //     []
    // );

    const handleValuesChange: Handlers['handleCheckboxGroupChange'] = useCallback(
        (name) => (updatedValues) => setValues((val) => ({ ...val, [name]: updatedValues })),
        [],
    );

    const handleCheckboxChange: Handlers['handleCheckboxChange'] = useCallback(
        (name) => (event) => {
            const { checked } = event.target as HTMLInputElement;
            setValues((val) => ({ ...val, [name]: checked }));
        },
        [],
    );

    const handleRadioGroupChange: Handlers['handleRadioGroupChange'] = useCallback(
        (name, onChange) => (updatedValues) => {
            setValues((val) => ({ ...val, [name]: updatedValues }));
            onChange && onChange(updatedValues);
        },
        [],
    );

    const handleDateChange: Handlers['handleDateChange'] = useCallback(
        (name, onChange) => (value) => {
            setValues((val) => ({ ...val, [name]: value ? value[0] : '' }));
            onChange && onChange(value);
        },
        [],
    );

    const handleDateRangeChange: Handlers['handleDateRangeChange'] = useCallback(
        (name) => (value) => {
            setValues((val) => ({
                ...val,
                [`${name}.startDate`]: value.startDate,
                [`${name}.endDate`]: value.endDate,
            }));
        },
        [],
    );

    const handleFileChange: Handlers['handleFileChange'] = useCallback(
        (name) => (value) => {
            const { result } = value;
            if (result) {
                setValues((val) => ({
                    ...val,
                    [`${name}.tempFileId`]: result.id || result.itemId,
                    [`${name}.filename`]: result.name || result.filename,
                    [`${name}.url`]: result.url,
                }));
            }
        },
        [],
    );

    const handleFileSelect: Handlers['handleFileSelect'] = useCallback(
        (name) => () => {
            const itemToClick = document.getElementById(name),
                evt = new MouseEvent('click', {
                    bubbles: false,
                    cancelable: true,
                    view: window,
                });
            itemToClick?.dispatchEvent(evt);
        },
        [],
    );

    const handleRemoveField: Handlers['handleRemoveField'] = useCallback(
        () => (name) => {
            setValues((val) =>
                Object.keys(val || {})
                    .filter((key) => key !== name)
                    .reduce((acc: Record<string, unknown>, key: string) => {
                        acc[key] = val[key];
                        return acc;
                    }, {}),
            );
        },
        [],
    );

    const handleHTMLEditorChange: Handlers['handleHTMLEditorChange'] = useCallback(
        (name, onChange) => (event, editor) => {
            const data = editor.getData();
            setValues((val) => ({ ...val, [name]: data }));
            onChange && onChange(event, editor);
        },
        [],
    );

    const handleSingleSelectChange: Handlers['handleSingleSelectChange'] = useCallback(
        (name, onChange) => (event) => {
            const { value } = event;
            setValues((val) => ({ ...val, [name]: value }));
            onChange && onChange(event);
        },
        [],
    );

    const handleMultiSelectChange: Handlers['handleMultiSelectChange'] = useCallback(
        (name, onChange) => (event) => {
            const selected = [''];
            selected.length = 0;
            event.forEach((element) => {
                const { value } = element;
                selected.push(value);
            });
            setValues((val) => ({ ...val, [name]: selected }));
            onChange && onChange(event);
        },
        [],
    );

    return {
        formKey,
        values,
        setValues,
        errorMessages,
        addErrorMessage,
        setErrorMessages,
        handlers: {
            handleFormReset,
            handleFormSubmit,
            handleFocus,
            handleTextChange,
            handleNumberChange,
            handleFileChange,
            handleCheckboxChange,
            handleDateChange,
            handleDateRangeChange,
            getPeriodFromDates,
            handleCheckboxGroupChange: handleValuesChange,
            handleRadioGroupChange,
            handleSingleSelectChange,
            handleMultiSelectChange,
            handleChange,
            handleRemoveField,
            handleFileSelect,
            handleHTMLEditorChange,
            getBlobValues,
            handleTagChange,
        },
    };
};
